import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as Messages from '../constants/message.constant';
import { isNullOrEmpty } from '../utils/common-functions';

/**
 * 訊息彈窗服務
 *
 * @export
 * @class HintDialogService
 */
@Injectable({
  providedIn: 'root',
})
export class HintDialogService {
  private requireHintDialogSource = new Subject<HintDialogOption | null>();
  private clearHintDialogSource = new Subject();
  requireHintDialog$ = this.requireHintDialogSource.asObservable();
  clearHintDialogSource$ = this.clearHintDialogSource.asObservable();

  /**
   * 顯示訊息彈窗
   *
   * @param {HintDialogOption} option 彈窗設定
   * @memberof HintDialogService
   */
  showHint(option: HintDialogOption): void {
    this.requireHintDialogSource.next(option);
  }

  /**
   * 顯示確認彈窗
   *
   * @param {string} [summary]
   * @param {() => void} [acceptFunc] 按下確定 callback
   * @param {() => void} [rejectFunc] 按下取消 callback
   * @memberof HintDialogService
   */
  showConfirmHint(
    summary?: string,
    acceptFunc?: (event: any) => void,
    rejectFunc?: (event: any) => void
  ): void {
    const option: HintDialogOption = {
      headerIcon: 'pi pi-warning',
      hintType: 'confirm',
      header: 'System Notification',
      message: { category: summary },
      confirmText: 'Confirm',
      acceptFunc: acceptFunc,
      rejectFunc: rejectFunc,
    };
    this.requireHintDialogSource.next(option);
  }

  /**
   * 顯示列表訊息彈窗
   *
   * @param {MessageItem[]} messages 訊息列表
   * @param {string} [summary] 訊息摘要
   * @memberof HintDialogService
   */
  showListHint(messages: MessageItem[], summary?: string): void {
    const option: HintDialogOption = {
      headerIcon: 'pi pi-warning',
      hintType: 'list',
      header: 'System Notification',
      messages,
      cancelText: 'OK',
    };
    if (isNullOrEmpty(summary) === false) {
      option.message = { category: summary };
    }
    this.requireHintDialogSource.next(option);
  }

  /**
   * 顯示錯誤訊息彈窗
   *
   * @param {string} [summary] 訊息摘要
   * @param {string} [message] 詳細訊息(可折疊)
   * @param {() => void} [callback] 關閉彈窗 callback
   * @memberof HintDialogService
   */
  showErrorHint(
    summary?: string,
    message?: string,
    callback?: () => void
  ): void {
    const option: HintDialogOption = {
      headerIcon: 'pi pi-warning',
      hintType: 'message',
      header: 'System Notification',
      message: { category: summary, message },
      cancelText: 'Confirm',
      rejectFunc: callback,
    };
    this.requireHintDialogSource.next(option);
  }

  /**
   * 顯示資料格式錯誤彈窗
   *
   * @param {MessageItem[]} messages 資料格式錯誤訊息列表
   * @param {string} [summary] 資料格式錯誤訊息摘要
   * @memberof HintDialogService
   */
  showDataFormatErrorHint(messages: MessageItem[], summary?: string): void {
    const option: HintDialogOption = {
      headerIcon: 'pi pi-warning',
      hintType: 'list',
      header: 'System Notification',
      messages,
      cancelText: 'Confirm',
    };
    if (isNullOrEmpty(summary) === false) {
      option.message = { category: summary };
    }
    this.requireHintDialogSource.next(option);
  }

  /**
   * 顯示不儲存並離開提示彈窗
   *
   * @param {() => void} [acceptFunc] 按下確定 callback
   * @param {() => void} [rejectFunc] 按下取消 callback
   * @memberof HintDialogService
   */
  showExitWithoutSavingHint(
    acceptFunc?: () => void,
    rejectFunc?: () => void
  ): void {
    const option: HintDialogOption = {
      headerIcon: 'pi pi-warning',
      hintType: 'confirm',
      header: 'System Notification',
      message: { category: Messages.EXIT_HINT_WITHOUT_SAVING_MSG },
      acceptFunc,
      rejectFunc,
    };
    this.requireHintDialogSource.next(option);
  }
  /**
   * 僅顯示確認按鈕訊息視窗
   *
   * @param {string} [message] 訊息摘要
   * @param {() => void} [acceptFunc] 確認彈窗 callback
   * @memberof HintDialogService
   */
  showOnlyConfirmHint(message?: string, acceptFunc?: () => void): void {
    const option: HintDialogOption = {
      headerIcon: 'pi pi-confirm',
      hintType: 'onlyConfirm',
      header: 'System Notification',
      acceptFunc,
    };
    if (isNullOrEmpty(message) === false) {
      option.message = { category: message };
    }
    this.requireHintDialogSource.next(option);
  }

  /**
   * 清除所有彈窗
   *
   * @memberof HintDialogService
   */
  clear(): void {
    this.clearHintDialogSource.next(null);
  }
}

export interface HintDialogOption {
  headerIcon?: string;
  message?: MessageItem;
  messages?: MessageItem[];
  header?: string;
  hintType?: 'list' | 'message' | 'confirm' | 'onlyConfirm';
  confirmText?: string;
  cancelText?: string;
  acceptFunc?: (event: any) => void;
  rejectFunc?: (event: any) => void;
}

export interface MessageItem {
  category?: string;
  message?: string;
  success?: boolean;
}
